import React from 'react';
import { Input } from 'components/forms/Input';
import { FormModal } from 'components/containers/FormModal';
import type { UseFormReturn } from 'react-hook-form';
import { RegisterOptions } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form/dist/types/form';
import { getHashtagsCount } from 'helpers/getHashTagsCount';
import classes from './CreateEditSnippetModal.module.scss';
import classNames from 'classnames';
import { InputTypes } from '../../../../constants/form';
import { CreateSnippetInput } from '../../../../models/graphql';
import { Tooltip } from 'components/actions/Tooltip';
import { HASHTAG_SEARCH_URL } from '../../../../constants/hashtag-search';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { footerModalButton } from 'components/containers/Modal/Modal.view';
import { ButtonColorsEnum } from 'constants/button-colors';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';

export type CreateEditSnippetModalViewProps = {
  formTitle: string;
  form: UseFormReturn<CreateSnippetInput>;
  onSubmit: SubmitHandler<CreateSnippetInput>;
  onClose: VoidFunction;
  loading: boolean;
  nameRules: RegisterOptions;
  contentRules: RegisterOptions;
  // those are used by label-counter inside content textarea
  maxHashtagCount: number;
  maxContentLength: number;
};

type HashtagAndLengthCounterProps = {
  form: UseFormReturn;
  maxHashtagCount: number;
  maxContentLength: number;
};

export const CreateEditSnippetModalView = (props: CreateEditSnippetModalViewProps): JSX.Element => {
  const { formTitle, form, onSubmit, onClose, loading, nameRules, contentRules, maxHashtagCount, maxContentLength } =
    props;

  // we need to cast UseFormReturn<T> back to UseFormReturn
  const formInstance: UseFormReturn = form as unknown as UseFormReturn;

  return (
    <FormModal
      showCloseIcon
      loading={loading}
      onSubmit={form.handleSubmit(onSubmit)}
      formInstance={formInstance}
      title={formTitle}
      submitText={'Save Snippet'}
      footerContent={() => {
        return (
          <div className={classes.FooterModal}>
            <a
              id={'hashtagSearch'}
              href={HASHTAG_SEARCH_URL}
              target={'_blank'}
              rel={'noreferrer'}
              className={'grid-x align-middle margin-bottom-small-12'}>
              <div className={classNames('cell', 'shrink', classes.CreateEditSnippetModalHashtagIcon)}>
                <Icon name={IconsEnum.hashtag} size={'medium'} />
              </div>
              <div className={'cell shrink align-middle'}>
                Hashtag search{' '}
                <Tooltip
                  pointer
                  target={'#hashtagSearchTooltip'}
                  type={'inlined'}
                  autoZIndex={false}
                  style={{ zIndex: scssVars['z-index-overmodal'] }}>
                  Look up popular hashtags with this external tool. Simply enter 1-3 terms to get recommended hashtags,
                  pick your favorites, and copy/paste them into a Snippet above.
                </Tooltip>
              </div>
            </a>
            {footerModalButton(
              'Save Snippet',
              true,
              true,
              false,
              ButtonColorsEnum.secondary,
              'large',
              form.handleSubmit(onSubmit)
            )}
          </div>
        );
      }}
      onClose={onClose}>
      <div className={'padding-bottom-small-20'}>
        <Input
          inlinedLabel
          disabled={loading}
          formInstance={formInstance}
          name={'name'}
          type={InputTypes.text}
          size={'medium'}
          label={'Snippet Name'}
          rules={nameRules}
        />
      </div>

      <div className={classNames('padding-bottom-small-20', classes.CreateEditSnippetModalContentTextArea)}>
        <Input
          labelRightSlot={
            <HashtagAndLengthCounter
              form={formInstance}
              maxHashtagCount={maxHashtagCount}
              maxContentLength={maxContentLength}
            />
          }
          inputWrapperClassName={classes.CreateEditSnippetModalContentTextAreaInputWrapper}
          multiline
          disabled={loading}
          formInstance={formInstance}
          name={'content'}
          type={InputTypes.text}
          size={'medium'}
          label={'Content'}
          rules={contentRules}
        />
      </div>
    </FormModal>
  );
};

// just a small render helper to reduce amount of rerenders due to watch fn;
const HashtagAndLengthCounter = ({ form, maxHashtagCount, maxContentLength }: HashtagAndLengthCounterProps) => {
  const content = form.watch('content');

  const currentContentLength = content?.length || 0;
  const currentHashtagCount = getHashtagsCount(content);

  const remainingContentCharsCount = maxContentLength - currentContentLength;

  return (
    <div className={classNames(classes.CreateEditSnippetModalCounter)}>
      <span
        className={classNames(classes.CreateEditSnippetModalCounterHashtag, {
          [classes.CreateEditSnippetModalCounterHasError]: currentHashtagCount > maxHashtagCount
        })}>
        &#35;{currentHashtagCount}&#47;{maxHashtagCount}
      </span>
      <span
        className={classNames(classes.CreateEditSnippetModalCounterContentLength, {
          [classes.CreateEditSnippetModalCounterHasError]: remainingContentCharsCount < 0
        })}>
        {maxContentLength - currentContentLength}
      </span>
    </div>
  );
};
